import { type FC, type ReactNode, useContext } from 'react';
import classnames from 'classnames';
import { useFragment, graphql } from 'react-relay';

import { FormattedMessage } from 'dibs-react-intl';
import Package from 'dibs-icons/exports/Package.ts';
import Article from 'dibs-icons/exports/Article.ts';
import Person from 'dibs-icons/exports/Person.ts';
import CreditCard from 'dibs-icons/exports/CreditCard.ts';
import ShoppingBag from 'dibs-icons/exports/ShoppingBag.ts';
import Storefront from 'dibs-icons/exports/Storefront.ts';
import Message from 'dibs-icons/exports/Message.ts';
import Finances from 'dibs-icons/exports/Finances.ts';
import Shipping from 'dibs-icons/exports/Shipping.ts';
import Webinars from 'dibs-icons/exports/Webinars.ts';
import CheckShield from 'dibs-icons/exports/CheckShield.ts';
import { GetHelpZendeskIntroLink } from './GetHelpZendeskIntroLink.tsx';

import { trackGetHelpZendeskEvent } from './getHelpZendeskTracking.ts';
import { GetHelpZendeskContext } from './GetHelpZendeskContext.ts';
import { ACTIONS, STEPS } from './GetHelpZendeskConstants.ts';

import { type GetHelpZendeskBrowseByTopic_viewer$key } from './__generated__/GetHelpZendeskBrowseByTopic_viewer.graphql.ts';

import dibsCss from 'dibs-css';

const viewerFragment = graphql`
    fragment GetHelpZendeskBrowseByTopic_viewer on Viewer {
        zendeskSupport(isSeller: $isSeller, sellerId: $sellerId) {
            categories {
                serviceId
                displayName
            }
        }
    }
`;

const CATEGORY_ICON_MAP: { [key: string]: ReactNode } = {
    // buyer
    '14416666612763': <Article variation="24" />,
    '14416669159067': <Package variation="24" />,
    '14416687802907': <CreditCard variation="24" />,
    '13931499721883': <Person variation="24" />,
    '14331958044187': <ShoppingBag variation="24" />,
    '14480919967643': <Storefront variation="24" />,
    // seller
    '14318093035675': <Article variation="24" />,
    '14720404301083': <Person variation="24" />,
    '14720452875163': <CreditCard variation="24" />,
    '14720424225819': <ShoppingBag variation="24" />,
    '14720425546395': <Message variation="24" />,
    '14720426741531': <Finances variation="24" />,
    '14720474402843': <Shipping variation="24" />,
    '14720476288283': <Webinars variation="24" />,
    '14720497602203': <CheckShield variation="24" />,
};

const GetHelpZendeskBrowseByTopic: FC<{ viewer: GetHelpZendeskBrowseByTopic_viewer$key }> = ({
    viewer: viewerRef,
}) => {
    const {
        dispatch,
        state: { isSeller },
    } = useContext(GetHelpZendeskContext);

    const viewer = useFragment(viewerFragment, viewerRef);
    const { zendeskSupport } = viewer;
    const zendeskCategories = zendeskSupport?.categories || [];

    return (
        <>
            <div
                className={classnames(
                    dibsCss.sassyFontBodyMediumHeavy,
                    dibsCss.textIron,
                    dibsCss.mtLarge
                )}
            >
                <FormattedMessage
                    id="getHelp.GetHelpZendeskBrowseByTopic.browseByTopic"
                    defaultMessage="Browse by Topic"
                />
            </div>
            {zendeskCategories.map((category, index) => (
                <GetHelpZendeskIntroLink
                    key={category?.serviceId}
                    isLast={index === zendeskCategories.length - 1}
                    onClick={() => {
                        trackGetHelpZendeskEvent({
                            stepInteractionName: 'zendesk category click',
                            trigger: category?.displayName || '',
                            isSeller,
                        });
                        dispatch({ type: ACTIONS.SET_STEP, step: STEPS.CATEGORY });
                        dispatch({
                            type: ACTIONS.SET_CATEGORY_ID,
                            categoryId: category?.serviceId || '',
                        });
                    }}
                    icon={CATEGORY_ICON_MAP[category?.serviceId || '']}
                >
                    {category?.displayName}
                </GetHelpZendeskIntroLink>
            ))}
        </>
    );
};

export { GetHelpZendeskBrowseByTopic };
